 import React, { useState, useCallback } from 'react';
import {
  visitRequests as visitsApi,
  pastoralVisits as pastoralVisitsApi,
  counselingSessions as counselingApi,
  sickLogs as sickLogsApi
} from '../../api/endpoints';
import {
  useMultiLedger, useSearch, usePagination, useNotification, useOptionList, useDateRangeFilter,
  useOpenRecordFromLink, findLedgerRow, RECORD_GONE
} from '../../hooks';
import { formatDate } from '../../utils/format';
import type { RecordStatus } from '../../types';
import useStyles from '../../theme/useStyles';
import { missingRequired, requiredError } from '../../utils/requiredFields';
import {
  PageHeader, SubTabs, SearchBar, DateRangeFilter, Pagination, Notification, PromptDialog, FormLayout, Input, SearchableSelect, Badge, Button, TablePanel, Modal
} from '../../components/ui';
import PrintButton from '../../components/ui/PrintButton';
import PastoralSummary from './PastoralSummary';

const EMPTY_VISIT = { date: '', memberName: '', pastorName: '', location: '', purpose: '', notes: '' };
const EMPTY_PASTORAL_VISIT = { date: '', memberName: '', pastorName: '', location: '', purpose: '', notes: '' };
const EMPTY_COUNSELING = { date: '', memberName: '', pastorName: '', location: '', type: '', sessionStatus: '', notes: '' };
const EMPTY_SICK = { date: '', memberName: '', pastorName: '', location: '', followUpDate: '', notes: '' };

// Visit requests raised in the mobile app use their own column names, so the
// stored columns are mapped onto the field names this screen renders.
// `assignedPastor` and `pastorNotes` are the real columns — writing
// `pastorName` / `notes` instead means the server silently drops them.
const mapVisit = (v) => ({
  ...v,
  recordId: v.recordId || `MOB-${v.id}`,
  memberName: v.requestedBy || v.memberName,
  date: v.preferredDate || v.date,
  purpose: v.requestType || v.purpose,
  pastorName: v.assignedPastor || 'Pending Assignment',
  location: v.location || 'Awaiting Details',
  notes: v.pastorNotes || v.userNotes || '',
  status: v.status || 'PENDING'
});

/**
 * The four sibling resources behind this screen. `visits` is the inbox of
 * requests raised in the mobile app; `pastoralVisits` is the register of visits
 * the church actually carried out.
 */
type PastoralTab = 'visits' | 'pastoralVisits' | 'counseling' | 'sick';
/** The Summary tab reads all four; the rest of the screen works on one. */
type PastoralView = PastoralTab | 'summary';

/**
 * What each tab puts on paper. The tabs store different columns, so each
 * prints its own register; a single shared layout printed blank cells on
 * every tab but the one it was written for.
 */
const TAB_INFO: Record<PastoralTab, { documentType: string; title: string; recordType: string }> = {
  visits: { documentType: 'PASTORAL_CARE_REGISTER', title: 'Visit Requests', recordType: 'Visit Request' },
  pastoralVisits: { documentType: 'PASTORAL_VISIT_REGISTER', title: 'Pastoral Visits Done', recordType: 'Pastoral Visit' },
  counseling: { documentType: 'COUNSELING_REGISTER', title: 'Counselling Sessions', recordType: 'Counselling Session' },
  sick: { documentType: 'SICK_TRACKING_REGISTER', title: 'Sick Member Tracking', recordType: 'Sick Member Log' }
};

const STAGE_LABELS: Record<string, string> = {
  COMPLETED: 'Completed', IN_PROGRESS: 'In Progress', PENDING: 'Pending', ACTIVE: 'Scheduled'
};

const PastoralCare = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;

  const [activeView, setActiveView] = useState<PastoralView>('summary');
  const isSummary = activeView === 'summary';
  const activeTab: PastoralTab = isSummary ? 'visits' : activeView;
  const [viewMode, setViewMode] = useState('history');
  const [visitForm, setVisitForm] = useState(EMPTY_VISIT);
  const [pastoralVisitForm, setPastoralVisitForm] = useState(EMPTY_PASTORAL_VISIT);
  const [counselingForm, setCounselingForm] = useState(EMPTY_COUNSELING);
  const [sickForm, setSickForm] = useState(EMPTY_SICK);
  // The row being edited on the open tab, by database key. Until now every
  // log was write-once: a wrong date or a typo in a name could only be voided
  // and re-entered.
  const [editingId, setEditingId] = useState<number | null>(null);
  // The row opened in the View dialog: every field it holds, and a print of it.
  const [viewing, setViewing] = useState(null);
  const isEditing = editingId !== null;
  const existingRow = (key: PastoralTab) =>
    (isEditing ? (ledger.active(key).find((r) => r.rawId === editingId) as { recordId?: string; status?: string } | undefined) : undefined);
  const existingRecordId = (key: PastoralTab) => existingRow(key)?.recordId ?? null;
  /** An edit keeps the record's stage (In Progress stays In Progress); a new record starts Scheduled. */
  const stageOf = (key: PastoralTab): RecordStatus => (existingRow(key)?.status === 'IN_PROGRESS' ? 'IN_PROGRESS' : 'ACTIVE');

  const isDeletedView = viewMode === 'deleted';
  const { notification, notify, dismiss } = useNotification();

  const ledger = useMultiLedger({
    visits: { resource: visitsApi, label: 'Visit Request', prefix: 'VIS', map: mapVisit },
    pastoralVisits: { resource: pastoralVisitsApi, label: 'Pastoral Visit', prefix: 'PVS' },
    counseling: { resource: counselingApi, label: 'Counseling', prefix: 'CNS', statusField: 'sessionStatus' },
    sick: { resource: sickLogsApi, label: 'Sick Tracking', prefix: 'SCK' }
  }, { notify });

  // All three tabs draw pastors from one roster.
  const pastors = useOptionList('church_pc_pastors');
  const visitPurposes = useOptionList('church_pc_visit_purposes');
  const counselingTypes = useOptionList('church_pc_couns_types');
  const counselingStatuses = useOptionList('church_pc_couns_statuses');

  // A record is COMPLETED once the visit has happened. The ledger only splits
  // live from VOID, so completed rows are filtered out of the active list here
  // and given their own view.
  const isCompletedView = viewMode === 'completed';
  const statusFieldFor = (tab: PastoralTab) =>
    (tab === 'counseling' ? 'sessionStatus' : 'status');
  const statusOf = (row) => row?.[statusFieldFor(activeTab)];

  const dataset = isDeletedView
    ? ledger.voided(activeTab)
    : isCompletedView
      ? ledger.active(activeTab).filter((r) => statusOf(r) === 'COMPLETED')
      : ledger.active(activeTab).filter((r) => statusOf(r) !== 'COMPLETED');

  /**
   * The flow is Scheduled → In Progress → Completed (CHM-073): a record can only
   * be completed once it has been started, so "Completed" is never the first
   * click on a fresh entry. Counselling keeps its own session status (Ongoing /
   * Resolved...) and completes directly, as before.
   */
  const hasStartStep = activeTab !== 'counseling';
  const isInProgress = (row) => statusOf(row) === 'IN_PROGRESS';

  const setStage = async (row, stage: 'IN_PROGRESS' | 'COMPLETED') => {
    const field = statusFieldFor(activeTab);
    const payload = { ...row, id: row.rawId, [field]: stage };
    if (await ledger.save(activeTab, payload, row.rawId)) {
      notify(stage === 'COMPLETED' ? 'Record marked as completed.' : 'Record is now in progress.', 'success');
    }
  };
  const markInProgress = (row) => setStage(row, 'IN_PROGRESS');
  const markCompleted = (row) => setStage(row, 'COMPLETED');

  const getRowDate = useCallback((r) => r.date, []);
  const range = useDateRangeFilter(dataset, getRowDate);
  const { searchTerm, setSearchTerm, filtered, clear } = useSearch(
    range.filtered, ['recordId', 'memberName', 'pastorName', 'location', 'purpose', 'type']
  );
  const page = usePagination(filtered);

  const resetForms = () => {
    setVisitForm(EMPTY_VISIT);
    setPastoralVisitForm(EMPTY_PASTORAL_VISIT);
    setCounselingForm(EMPTY_COUNSELING);
    setSickForm(EMPTY_SICK);
    setEditingId(null);
  };

  /** Loads a row into its tab's form. Requests are shown through mapVisit, so the mapped names are what the form holds. */
  const startEdit = (row) => {
    const base = { date: row.date || '', memberName: row.memberName || '', pastorName: row.pastorName || '', location: row.location || '', notes: row.notes || '' };
    if (activeTab === 'visits') {
      setVisitForm({ ...base, purpose: row.purpose || '', pastorName: row.assignedPastor || '', notes: row.pastorNotes || '' });
    } else if (activeTab === 'pastoralVisits') {
      setPastoralVisitForm({ ...base, purpose: row.purpose || '' });
    } else if (activeTab === 'counseling') {
      setCounselingForm({ ...base, type: row.type || '', sessionStatus: row.sessionStatus || '' });
    } else {
      setSickForm({ ...base, followUpDate: row.followUpDate || '' });
    }
    setEditingId(row.rawId);
    setViewMode('add');
  };

  const switchTab = (tab) => {
    setActiveView(tab);
    setViewMode('history');
    resetForms();
    page.reset();
    clear();
    range.clear();
  };

  const switchView = (mode) => {
    if (mode !== 'add') resetForms();
    setViewMode(mode);
    page.reset();
  };

  // "Open the record" from the Approvals inbox: its tab (Deleted when it is
  // gone), then its full record.
  useOpenRecordFromLink(({ id, ref }) => {
    const row = findLedgerRow(ledger.rows, id, ref);
    if (!row) { notify(RECORD_GONE, 'error'); return; }
    const key = row.__key as PastoralTab;
    switchTab(key);
    if ((ledger.voided(key) as unknown[]).includes(row)) setViewMode('deleted');
    setViewing(row);
  }, !ledger.loading);

  const handleVisitSubmit = async (e) => {
    e.preventDefault();
    const purpose = visitForm.purpose.trim();
    const pastorName = visitForm.pastorName.trim();
    const missing = missingRequired([[purpose, 'Purpose'], [pastorName, 'Pastor Name']]);
    if (missing.length) { notify(requiredError(missing), 'error'); return; }

    visitPurposes.ensure(purpose);
    pastors.ensure(pastorName);

    // Written back in the mobile app's column names. `assignedPastor` and
    // `pastorNotes` are the stored columns — sending pastorName/notes instead
    // means the server drops them without an error.
    const payload = {
      requestedBy: visitForm.memberName,
      preferredDate: visitForm.date,
      requestType: purpose,
      location: visitForm.location,
      assignedPastor: pastorName,
      pastorNotes: visitForm.notes,
      recordId: existingRecordId('visits') || ledger.nextSequence('visits'),
      status: stageOf('visits')
    };

    if (await ledger.save('visits', payload, editingId)) { resetForms(); setViewMode('history'); }
  };

  const handlePastoralVisitSubmit = async (e) => {
    e.preventDefault();
    const purpose = pastoralVisitForm.purpose.trim();
    const pastorName = pastoralVisitForm.pastorName.trim();
    const missing = missingRequired([[purpose, 'Purpose'], [pastorName, 'Pastor Name']]);
    if (missing.length) { notify(requiredError(missing), 'error'); return; }

    visitPurposes.ensure(purpose);
    pastors.ensure(pastorName);

    // Native column names — no mobile-app mapping needed here.
    const payload = {
      ...pastoralVisitForm,
      purpose,
      pastorName,
      recordId: existingRecordId('pastoralVisits') || ledger.nextSequence('pastoralVisits'),
      status: stageOf('pastoralVisits')
    };

    if (await ledger.save('pastoralVisits', payload, editingId)) { resetForms(); setViewMode('history'); }
  };

  const handleCounselingSubmit = async (e) => {
    e.preventDefault();
    const type = counselingForm.type.trim();
    const sessionStatus = counselingForm.sessionStatus.trim();
    const pastorName = counselingForm.pastorName.trim();
    const missing = missingRequired([
      [type, 'Category'], [sessionStatus, 'Status'], [pastorName, 'Pastor Name']
    ]);
    if (missing.length) { notify(requiredError(missing), 'error'); return; }

    counselingTypes.ensure(type);
    counselingStatuses.ensure(sessionStatus);
    pastors.ensure(pastorName);

    const payload = {
      ...counselingForm,
      type,
      sessionStatus,
      pastorName,
      recordId: existingRecordId('counseling') || ledger.nextSequence('counseling'),
      status: 'ACTIVE' as const
    };

    if (await ledger.save('counseling', payload, editingId)) { resetForms(); setViewMode('history'); }
  };

  const handleSickSubmit = async (e) => {
    e.preventDefault();
    const pastorName = sickForm.pastorName.trim();
    if (!pastorName) { notify('Pastor Name is required.', 'error'); return; }
    pastors.ensure(pastorName);

    const payload = {
      ...sickForm,
      pastorName,
      recordId: existingRecordId('sick') || ledger.nextSequence('sick'),
      status: stageOf('sick')
    };

    if (await ledger.save('sick', payload, editingId)) { resetForms(); setViewMode('history'); }
  };

  const sub = { fontSize: '11px', color: t.textFaint };

  const statusLabelOf = (r) => {
    if (isDeletedView) return 'Deleted';
    const raw = String(statusOf(r) || '');
    if (activeTab === 'counseling') return raw === 'COMPLETED' ? 'Completed' : raw || '-';
    return STAGE_LABELS[raw] || 'Scheduled';
  };

  /**
   * A row as it prints: formatted dates, the status in words, and the request
   * tab's own column names filled even when only the mapped names are present.
   */
  const printRowOf = (r) => ({
    ...r,
    recordType: TAB_INFO[activeTab].recordType,
    date: formatDate(r.date),
    preferredDate: formatDate(r.preferredDate || r.date),
    requestedBy: r.requestedBy || r.memberName,
    assignedPastor: r.assignedPastor || r.pastorName,
    requestType: r.requestType || r.purpose,
    purpose: r.purpose || r.type || '-',
    sessionStatus: r.sessionStatus === 'COMPLETED' ? 'Completed' : (r.sessionStatus || '-'),
    followUpDate: formatDate(r.followUpDate),
    contactNumber: r.contactNumber || '-',
    notes: r.notes || '-',
    statusLabel: statusLabelOf(r),
    createdBy: r.createdBy || '-'
  });

  /** The View dialog's lines: only the fields this tab actually records. */
  const viewFieldsOf = (r): Array<[string, string]> => {
    const p = printRowOf(r);
    const isRequest = activeTab === 'visits';
    const lines: Array<[string, string | undefined]> = [
      ['Record ID', p.recordId],
      ['Record Type', p.recordType],
      [isRequest ? 'Preferred Date' : 'Date', p.date],
      [isRequest ? 'Requested By' : 'Member', p.memberName],
      ...(isRequest ? [['Contact Number', p.contactNumber] as [string, string]] : []),
      [isRequest ? 'Assigned Pastor' : 'Pastor', p.pastorName],
      ['Location / Place', p.location],
      ...(activeTab === 'counseling'
        ? [['Type', r.type], ['Session Status', p.sessionStatus]] as Array<[string, string]>
        : activeTab === 'sick'
          ? [['Follow-up Date', p.followUpDate]] as Array<[string, string]>
          : [[isRequest ? 'Request Type' : 'Purpose', p.purpose]] as Array<[string, string]>),
      ...(isRequest ? [['Notes from the Member', r.userNotes] as [string, string]] : []),
      [isRequest ? "Pastor's Notes" : 'Notes', isRequest ? r.pastorNotes : p.notes],
      ['Status', p.statusLabel],
      ['Created By', p.createdBy],
      ...(r.deletedAt ? [['Deleted On', r.deletedAt] as [string, string]] : []),
      ...(r.deleteReason ? [['Delete Reason', r.deleteReason] as [string, string]] : [])
    ];
    return lines.map(([label, value]) => [label, value ? String(value) : '-']);
  };

  const viewButton = (r) => (
    <Button variant="ghost" size="sm" title="View full record" onClick={() => setViewing(r)}>👁️ View</Button>
  );

  const sharedColumns = [
    {
      key: 'date',
      header: 'Date',
      nowrap: true,
      render: (r) => (<><strong>{formatDate(r.date)}</strong><div style={sub}>{r.recordId}</div></>)
    },
    { key: 'memberName', header: 'Member Name', render: (r) => <strong>{r.memberName}</strong> },
    { key: 'pastorName', header: 'Pastor', render: (r) => <Badge>{r.pastorName}</Badge> },
    { key: 'location', header: 'Location / Place', render: (r) => r.location || '-' },
    // The notes were captured on the form but never shown in the table (CHM-023).
    { key: 'notes', header: 'Notes', wrap: true, render: (r) => r.notes || '-' }
  ];

  const tabColumn = {
    visits: { key: 'purpose', header: 'Purpose', render: (r) => r.purpose || '-' },
    pastoralVisits: { key: 'purpose', header: 'Purpose', render: (r) => r.purpose || '-' },
    counseling: {
      key: 'type',
      header: 'Type & Status',
      // 'COMPLETED' is the internal completion marker, surfaced by the dedicated
      // Status column in the Completed view — so it is not repeated here as a
      // session-status badge, which showed "Completed" twice on the row (CHM-073).
      render: (r) => (
        <>
          <div style={{ fontWeight: 'bold' }}>{r.type}</div>
          {r.sessionStatus !== 'COMPLETED' && (
            <Badge tone={String(r.sessionStatus || '').includes('Ongoing') ? 'success' : 'neutral'}>
              {r.sessionStatus || '-'}
            </Badge>
          )}
        </>
      )
    },
    sick: {
      key: 'followUpDate',
      header: 'Follow-up Date',
      render: (r) => (r.followUpDate
        ? <div style={{ fontSize: '13px', color: t.info, fontWeight: 'bold' }}>{formatDate(r.followUpDate)}</div>
        : '-')
    }
  }[activeTab];

  const tailColumns = isDeletedView
    ? [
        { key: 'deletedAt', header: 'Deleted On', render: (r) => <span style={sub}>{r.deletedAt || '-'}</span> },
        { key: 'deleteReason', header: 'Reason', wrap: true, render: (r) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{r.deleteReason || '-'}</span> },
        { key: 'actions', header: 'Actions', align: 'right' as const, render: viewButton }
      ]
    : isCompletedView
      ? [
          {
            key: 'completed',
            header: 'Status',
            align: 'right' as const,
            render: () => <Badge tone="success">✅ Completed</Badge>
          },
          { key: 'actions', header: 'Actions', align: 'right' as const, render: viewButton }
        ]
      : [
          ...(hasStartStep
            ? [{
                key: 'stage',
                header: 'Status',
                render: (r) => (isInProgress(r)
                  ? <Badge tone="warning">In Progress</Badge>
                  : <Badge tone="neutral">Scheduled</Badge>)
              }]
            : []),
          {
            key: 'actions',
            header: 'Actions',
            align: 'right' as const,
            render: (r) => (
              <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
                {viewButton(r)}
                <Button write variant="ghost" size="sm" onClick={() => startEdit(r)}>Edit</Button>
                {hasStartStep && !isInProgress(r) && (
                  <Button write variant="secondary" size="sm" onClick={() => markInProgress(r)} title="The visit has begun">▶ Start</Button>
                )}
                {(!hasStartStep || isInProgress(r)) && (
                  <Button write variant="secondary" size="sm" onClick={() => markCompleted(r)}>✅ Complete</Button>
                )}
                <Button variant="danger" size="sm" style={styles.deleteBtn} onClick={() => ledger.requestVoid(r)}>Delete</Button>
              </div>
            )
          }
        ];

  const columns = [...sharedColumns, tabColumn, { key: 'createdBy', header: 'Created By', render: (r) => (r as { createdBy?: string }).createdBy || '-' }, ...tailColumns];

  const pastorField = (value, onChange) => (
    <SearchableSelect
      label="Pastor Name" required placeholder="Search or type pastor..."
      value={value}
      onChange={onChange}
      options={pastors.options}
      onOptionsChange={pastors.setOptions}
    />
  );

  return (
    <div style={{ ...styles.container, minHeight: '80vh' }}>
      <Notification notification={notification} onClose={dismiss} />

      <PageHeader
        icon="🤲"
        title="Pastoral Care"
        activeTab={activeView}
        onTabChange={switchTab}
        tabs={[
          { key: 'summary', label: '📊 Summary' },
          { key: 'visits', label: '📥 Requests (from app)' },
          { key: 'pastoralVisits', label: '🏠 Visits Done' },
          { key: 'counseling', label: '🛋️ Counseling' },
          { key: 'sick', label: '🏥 Sick Member Tracking' }
        ]}
      />

      {isSummary && (
        <PastoralSummary
          requests={ledger.active('visits')}
          visits={ledger.active('pastoralVisits')}
          sessions={ledger.active('counseling')}
          sick={ledger.active('sick')}
          loading={ledger.loading}
          isDarkMode={isDarkMode}
        />
      )}

      {!isSummary && (
      <SubTabs
        value={viewMode}
        onChange={switchView}
        tabs={[
          { key: 'history', label: '📋 Active Logs' },
          { key: 'completed', label: '✅ Completed / Visited' },
          { key: 'deleted', label: '🗑️ Deleted', variant: 'danger' },
          { key: 'add', write: true, label: isEditing ? '✎ Editing Record...' : '+ Add New Entry' }
        ]}
      />
      )}

      {!isSummary && (viewMode === 'history' || isCompletedView || isDeletedView) && (
        <>
          <SearchBar
            value={searchTerm}
            onChange={(v) => { setSearchTerm(v); page.reset(); }}
            onClear={() => { clear(); page.reset(); }}
            placeholder="Search by Member, Pastor, Location or ID..."
            actions={(
              <>
                <DateRangeFilter {...range} onChange={page.reset} />
                  <PrintButton
                    module="Pastoral Care"
                    documentType={TAB_INFO[activeTab].documentType}
                    size="sm"
                    title={`Pastoral-Care-${TAB_INFO[activeTab].title.replace(/ /g, '-')}`}
                    data={() => ({
                      title: `Pastoral Care — ${TAB_INFO[activeTab].title}${isDeletedView ? ' (Deleted)' : isCompletedView ? ' (Completed)' : ''}`,
                      reference: [
                        searchTerm && `Search: "${searchTerm}"`,
                        `Printed ${formatDate(new Date().toISOString())}`
                      ].filter(Boolean).join(' · '),
                      // Every line that matches the filters, not just the page on screen.
                      rowCount: filtered.length,
                      rows: filtered.map(printRowOf)
                    })}
                    onNotify={notify}
                    isDarkMode={isDarkMode}
                  />
              </>
            )}
          />

          <TablePanel
            title={<>Pastoral Care Log</>}
            below={<Pagination {...page} itemsOnPage={page.currentItems.length} label="records" />}
              columns={columns}
              rows={page.currentItems}
              rowKey="rawId"
              loading={ledger.loading}
              emptyMessage="No records found for this category."
          />
        </>
      )}

      {!isSummary && viewMode === 'add' && activeTab === 'pastoralVisits' && (
        <FormLayout
          title={isEditing ? 'Edit — Record Home Visit' : 'Record Home Visit'}
          submitLabel={isEditing ? 'Update Record' : 'Save Visit Record'}
          onCancel={isEditing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
          submitting={ledger.saving}
          onSubmit={handlePastoralVisitSubmit}
        >
          <FormLayout.Grid>
            <Input
              label="Visit Date" type="date" required
              value={pastoralVisitForm.date}
              onChange={(e) => setPastoralVisitForm({ ...pastoralVisitForm, date: e.target.value })}
            />
            <Input
              label="Member / Family Name" required placeholder="e.g. The Smith Family"
              value={pastoralVisitForm.memberName}
              onChange={(e) => setPastoralVisitForm({ ...pastoralVisitForm, memberName: e.target.value })}
            />
            {pastorField(pastoralVisitForm.pastorName, (v) => setPastoralVisitForm((prev) => ({ ...prev, pastorName: v })))}
            <Input
              label="Location / Place" required placeholder="e.g. Member's Residence"
              value={pastoralVisitForm.location}
              onChange={(e) => setPastoralVisitForm({ ...pastoralVisitForm, location: e.target.value })}
            />
            <SearchableSelect
              label="Purpose of Visit" required placeholder="Search or type purpose..."
              value={pastoralVisitForm.purpose}
              onChange={(v) => setPastoralVisitForm((prev) => ({ ...prev, purpose: v }))}
              options={visitPurposes.options}
              onOptionsChange={visitPurposes.setOptions}
            />
            <Input
              label="Pastoral Notes / Outcomes" type="textarea" maxLength={500} rows={3} fullWidth
              placeholder="Confidential notes regarding the visit..."
              value={pastoralVisitForm.notes}
              onChange={(e) => setPastoralVisitForm({ ...pastoralVisitForm, notes: e.target.value })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      {!isSummary && viewMode === 'add' && activeTab === 'visits' && (
        <FormLayout
          title={isEditing ? 'Edit — Log Visit Request' : 'Log Visit Request'}
          submitLabel={isEditing ? 'Update Record' : 'Save Visit Request'}
          onCancel={isEditing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
          submitting={ledger.saving}
          onSubmit={handleVisitSubmit}
        >
          <FormLayout.Grid>
            <Input
              label="Visit Date" type="date" required
              value={visitForm.date}
              onChange={(e) => setVisitForm({ ...visitForm, date: e.target.value })}
            />
            <Input
              label="Member / Family Name" required placeholder="e.g. The Smith Family"
              value={visitForm.memberName}
              onChange={(e) => setVisitForm({ ...visitForm, memberName: e.target.value })}
            />
            {pastorField(visitForm.pastorName, (v) => setVisitForm((prev) => ({ ...prev, pastorName: v })))}
            <Input
              label="Location / Place" required placeholder="e.g. Member's Residence"
              value={visitForm.location}
              onChange={(e) => setVisitForm({ ...visitForm, location: e.target.value })}
            />
            <SearchableSelect
              label="Purpose of Visit" required placeholder="Search or type purpose..."
              value={visitForm.purpose}
              onChange={(v) => setVisitForm((prev) => ({ ...prev, purpose: v }))}
              options={visitPurposes.options}
              onOptionsChange={visitPurposes.setOptions}
            />
            <Input
              label="Pastoral Notes / Outcomes" type="textarea" maxLength={500} rows={3} fullWidth
              placeholder="Confidential notes regarding the visit..."
              value={visitForm.notes}
              onChange={(e) => setVisitForm({ ...visitForm, notes: e.target.value })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      {!isSummary && viewMode === 'add' && activeTab === 'counseling' && (
        <FormLayout
          title={isEditing ? 'Edit — Record Counseling Session' : 'Record Counseling Session'}
          submitLabel={isEditing ? 'Update Record' : 'Save Counseling Record'}
          onCancel={isEditing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
          submitting={ledger.saving}
          onSubmit={handleCounselingSubmit}
        >
          <FormLayout.Grid>
            <Input
              label="Session Date" type="date" required
              value={counselingForm.date}
              onChange={(e) => setCounselingForm({ ...counselingForm, date: e.target.value })}
            />
            <Input
              label="Member Name" required placeholder="e.g. John & Mary"
              value={counselingForm.memberName}
              onChange={(e) => setCounselingForm({ ...counselingForm, memberName: e.target.value })}
            />
            {pastorField(counselingForm.pastorName, (v) => setCounselingForm((prev) => ({ ...prev, pastorName: v })))}
            <Input
              label="Location / Place" required placeholder="e.g. Pastor's Office"
              value={counselingForm.location}
              onChange={(e) => setCounselingForm({ ...counselingForm, location: e.target.value })}
            />
            <SearchableSelect
              label="Category" required placeholder="Search or type category..."
              value={counselingForm.type}
              onChange={(v) => setCounselingForm((prev) => ({ ...prev, type: v }))}
              options={counselingTypes.options}
              onOptionsChange={counselingTypes.setOptions}
            />
            <SearchableSelect
              label="Status" required placeholder="Search or type status..."
              value={counselingForm.sessionStatus}
              onChange={(v) => setCounselingForm((prev) => ({ ...prev, sessionStatus: v }))}
              options={counselingStatuses.options}
              onOptionsChange={counselingStatuses.setOptions}
            />
            <Input
              label="Session Notes (Strictly Confidential)" type="textarea" maxLength={500} rows={3} fullWidth
              placeholder="Key takeaways or next steps..."
              value={counselingForm.notes}
              onChange={(e) => setCounselingForm({ ...counselingForm, notes: e.target.value })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      {!isSummary && viewMode === 'add' && activeTab === 'sick' && (
        <FormLayout
          title={isEditing ? 'Edit — Log Sick Visit / Tracking' : 'Log Sick Visit / Tracking'}
          submitLabel={isEditing ? 'Update Record' : 'Save Health Record'}
          onCancel={isEditing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
          submitting={ledger.saving}
          onSubmit={handleSickSubmit}
        >
          <FormLayout.Grid>
            <Input
              label="Date of Visit / Contact" type="date" required
              value={sickForm.date}
              onChange={(e) => setSickForm({ ...sickForm, date: e.target.value })}
            />
            <Input
              label="Member Name" required placeholder="e.g. Mary Johnson"
              value={sickForm.memberName}
              onChange={(e) => setSickForm({ ...sickForm, memberName: e.target.value })}
            />
            {pastorField(sickForm.pastorName, (v) => setSickForm((prev) => ({ ...prev, pastorName: v })))}
            <Input
              label="Location / Place" required placeholder="e.g. City Hospital, Room 402"
              value={sickForm.location}
              onChange={(e) => setSickForm({ ...sickForm, location: e.target.value })}
            />
            <Input
              label="Scheduled Follow-up Date" type="date"
              value={sickForm.followUpDate}
              onChange={(e) => setSickForm({ ...sickForm, followUpDate: e.target.value })}
            />
            <Input
              label="Health Status & Prayer Needs" type="textarea" maxLength={500} rows={3} fullWidth
              placeholder="Condition updates, prayer requests..."
              value={sickForm.notes}
              onChange={(e) => setSickForm({ ...sickForm, notes: e.target.value })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      {viewing && (
        <Modal
          open
          title={`${TAB_INFO[activeTab].recordType} — ${viewing.recordId || ''}`}
          onClose={() => setViewing(null)}
          isDarkMode={isDarkMode}
          width="600px"
          footer={
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setViewing(null)}>Close</Button>
              <PrintButton
                module="Pastoral Care"
                documentType="PASTORAL_CARE_RECORD"
                variant="primary"
                title={`Pastoral-Care-${viewing.recordId || viewing.rawId}`}
                data={() => ({ ...printRowOf(viewing), title: TAB_INFO[activeTab].recordType })}
                onNotify={notify}
                isDarkMode={isDarkMode}
              />
            </div>
          }
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: t.fontSize.md }}>
            <tbody>
              {viewFieldsOf(viewing).map(([label, value]) => (
                // Colours come from the theme: the modal panel sets only a
                // background, so unstyled text would stay dark in dark mode.
                <tr key={label} style={{ borderBottom: `1px solid ${t.border}` }}>
                  <td style={{ padding: '7px 10px', color: t.textMuted, width: '170px', verticalAlign: 'top' }}>{label}</td>
                  <td style={{
                    padding: '7px 10px', fontWeight: 'bold', verticalAlign: 'top',
                    color: label === 'Delete Reason' ? t.danger : t.textMain,
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                  }}>
                    {value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}

      <PromptDialog
        open={Boolean(ledger.voiding)}
        title={`Delete ${ledger.voiding?.recordType || ''} ${ledger.voiding?.recordId || ''}`}
        message="Please enter the reason for removing this record. It will be kept in Deleted History."
        placeholder="Reason for voiding"
        confirmLabel="Delete"
        requiredError="A reason is required to remove a record."
        onSubmit={(reason) => ledger.confirmVoid(reason)}
        onCancel={ledger.cancelVoid}
      />
    </div>
  );
};

export default PastoralCare;
